import { BaseMenu, BaseMenuItem } from '@components/BaseMenu';
import {
  ActionDrawerItem,
  MobileActionDrawer,
} from '@components/MobileActionDrawer/MobileActionDrawer';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { FolderPath, folderPathName } from '@lib/library/folderPath';
import CreateNewFolderOutlined from '@mui/icons-material/CreateNewFolderOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveOutlined from '@mui/icons-material/DriveFileMoveOutlined';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import MoreVertRounded from '@mui/icons-material/MoreVertRounded';
import classNames from 'classnames';
import { MouseEvent, useState } from 'react';
import { useFolderDialogs } from './FolderDialogs';
import css from './LibraryFolderBar.module.scss';

type FolderMenuAction = 'edit' | 'new-folder' | 'move' | 'delete';

/** One list for the desktop menu and the mobile drawer — `.claude/rules/context-menu-actions.md`. */
const FOLDER_MENU_ACTIONS: (ActionDrawerItem & { id: FolderMenuAction })[] = [
  { id: 'edit', label: 'Rename or edit', icon: <EditIcon fontSize="small" /> },
  {
    id: 'new-folder',
    label: 'New folder inside',
    icon: <CreateNewFolderOutlined fontSize="small" />,
  },
  { id: 'move', label: 'Move to…', icon: <DriveFileMoveOutlined fontSize="small" /> },
  { id: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, danger: true },
];

/** The ⋯ on a folder tile and on the open folder's breadcrumb. */
export function FolderMenuButton({
  path,
  className,
  vertical = false,
}: {
  path: FolderPath;
  className?: string;
  /** ⋮ rather than ⋯, to match the media rows' menus in the table. */
  vertical?: boolean;
}) {
  const isMobile = useIsMobile();
  const dialogs = useFolderDialogs();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleAction = (action: FolderMenuAction) => {
    switch (action) {
      case 'edit':
        return dialogs.editFolder(path);
      case 'new-folder':
        return dialogs.createFolder(path);
      case 'move':
        return dialogs.moveFolder(path);
      case 'delete':
        return dialogs.deleteFolder(path);
    }
  };

  const open = (event: MouseEvent<HTMLElement>) => {
    // The button sits on a tile that opens the folder when clicked.
    event.stopPropagation();
    event.preventDefault();
    if (isMobile) setDrawerOpen(true);
    else setAnchorEl(event.currentTarget);
  };

  return (
    <>
      <button
        type="button"
        className={classNames(css.menuButton, className)}
        aria-label={`Actions for ${folderPathName(path)}`}
        aria-haspopup="menu"
        onClick={open}
      >
        {vertical ? <MoreVertRounded fontSize="small" /> : <MoreHorizRounded fontSize="small" />}
      </button>
      {isMobile ? (
        <MobileActionDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          title={folderPathName(path)}
          actions={FOLDER_MENU_ACTIONS}
          onAction={handleAction}
        />
      ) : (
        <BaseMenu
          anchorEl={anchorEl}
          open={anchorEl !== null}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {FOLDER_MENU_ACTIONS.map((action) => (
            <BaseMenuItem
              key={action.id}
              icon={action.icon}
              danger={action.danger}
              onClick={() => {
                setAnchorEl(null);
                handleAction(action.id);
              }}
            >
              {action.label}
            </BaseMenuItem>
          ))}
        </BaseMenu>
      )}
    </>
  );
}
